'use client';

import { useCallback } from 'react';
import type { CartSubselection } from '@/features/totems/domain/model/cart-item';
import type { MenuSnapshotProduct } from '@/features/totems/domain/model/menu-snapshot';
import { shouldOpenProductSheet } from '@/features/totems/domain/service/product-interaction';
import { getProductDisplayName } from '@/features/totems/domain/service/product-name';
import { getProductStockLimit } from '@/features/totems/domain/service/product-stock';
import { useTotemActions, useTotemCartState } from '@/features/totems/presentation/providers/web-totem/totem-provider';

export function useProductInteraction() {
  const { cart } = useTotemCartState();
  const { setSelectedProduct, triggerFlyToCart } = useTotemActions();

  const addProductToCart = useCallback(
    (product: MenuSnapshotProduct, quantity: number, subselections: CartSubselection[], fromRect?: DOMRect) => {
      const stockLimit = getProductStockLimit(product.product_stock);
      const currentQuantity = cart.items
        .filter(item => item.productId === product.id)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (currentQuantity + quantity > stockLimit) {
        return;
      }

      cart.addItem({
        productId: product.id,
        name: getProductDisplayName(product),
        imageUrl: product.imageurl,
        unitPrice: Number(product.price),
        quantity,
        subselections,
      });

      if (fromRect && product.imageurl) {
        triggerFlyToCart({
          imageUrl: product.imageurl,
          fromRect,
        });
      }
    },
    [cart, triggerFlyToCart]
  );

  const handleProductClick = useCallback(
    (product: MenuSnapshotProduct, fromRect: DOMRect) => {
      const stockLimit = getProductStockLimit(product.product_stock);
      const currentQuantity = cart.items
        .filter(item => item.productId === product.id)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (currentQuantity >= stockLimit) {
        return;
      }

      if (shouldOpenProductSheet(product, cart.items)) {
        setSelectedProduct(product);
        return;
      }

      addProductToCart(product, 1, [], fromRect);
    },
    [cart.items, setSelectedProduct, addProductToCart]
  );

  return {
    handleProductClick,
    addProductToCart,
  };
}
